import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Trash2, Plus } from "lucide-react";
import { AnchoredList } from "@/components/AnchoredList";

const TARJETAS_TIPO = [
  { id: "codigo", testid: "plantilla-tipo-codigo", titulo: "Agregar por código",
    ayuda: "Repuesto Honda: se guarda su prefijo y cada modelo usa su propio código." },
  { id: "fijo", testid: "plantilla-tipo-fijo", titulo: "Agregar código fijo",
    ayuda: "Repuesto universal (aceite, limpiador): se copia tal cual en todos los modelos." },
  { id: "bloque", testid: "plantilla-tipo-bloque", titulo: "Agregar por bloque de categoría",
    ayuda: "Todas las piezas de un tipo en ciertos bloques del catálogo (ej. roles de la caja)." },
];

const Chip = ({ color, children }) => (
  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase font-semibold whitespace-nowrap ${color}`}>{children}</span>
);

export const PlantillaItemsEditor = ({ items, repuestos, esMaestra = false, onAgregarPrefijo, onAgregarFijo, onAgregarPrefijoManual, onAgregarGrupo, onAgregarRepuesto, onCantidad, onQuitar, onToggleOpcional, onToggleCantidadManual, onCantidadMaxima }) => {
  const [busqueda, setBusqueda] = useState("");
  const [tipo, setTipo] = useState("codigo");
  const [grupoPalabra, setGrupoPalabra] = useState("");
  const [grupoBloques, setGrupoBloques] = useState("");
  const buscarRef = useRef(null);
  const repIdx = Object.fromEntries(repuestos.map((r) => [r.id, r]));

  const prefijoCandidato = busqueda.trim().toUpperCase();
  const mostrarPrefijoManual = esMaestra && tipo === "codigo" && /^[A-Z0-9]{5}$/.test(prefijoCandidato);

  const filtrados = (() => {
    if (!busqueda) return [];
    const q = busqueda.toLowerCase().trim();
    const match = repuestos.filter((r) => (r.codigo_parte + " " + r.nombre).toLowerCase().includes(q));
    const empieza = (r) => r.codigo_parte.toLowerCase().startsWith(q) || r.nombre.toLowerCase().startsWith(q);
    match.sort((a, b) => {
      const ea = empieza(a), eb = empieza(b);
      if (ea !== eb) return ea ? -1 : 1;
      return a.nombre.localeCompare(b.nombre);
    });
    return match.slice(0, 50);
  })();

  const elegir = (rep) => {
    if (!esMaestra) onAgregarRepuesto(rep);
    else if (tipo === "fijo") onAgregarFijo(rep);
    else onAgregarPrefijo(rep);
    setBusqueda("");
  };

  const agregarManual = () => {
    if (mostrarPrefijoManual) { onAgregarPrefijoManual(prefijoCandidato); setBusqueda(""); }
  };

  const placeholder = !esMaestra ? "Buscar repuesto (se agrega el código completo)…"
    : tipo === "fijo" ? "Buscar repuesto universal por nombre o código…"
    : "Buscar repuesto por nombre o código, o escribir un prefijo (ej. 12391)…";

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-semibold">1. Agregar repuesto a la plantilla</Label>

        {esMaestra && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {TARJETAS_TIPO.map((t) => (
              <button key={t.id} type="button" data-testid={t.testid}
                onClick={() => { setTipo(t.id); setBusqueda(""); }}
                className={`text-left rounded-sm px-3 py-2 transition-colors ${
                  tipo === t.id ? "border-2 border-[#216295] bg-sky-50/50" : "border border-border hover:bg-secondary/50"}`}>
                <div className="text-sm font-semibold">{t.titulo}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{t.ayuda}</div>
              </button>
            ))}
          </div>
        )}

        {(!esMaestra || tipo === "codigo" || tipo === "fijo") && (
          <div className="relative" ref={buscarRef}>
            <Input data-testid="plantilla-buscar-repuesto" placeholder={placeholder}
              value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && mostrarPrefijoManual) { e.preventDefault(); agregarManual(); } }} />
            <AnchoredList anchorRef={buscarRef} open={filtrados.length > 0 || mostrarPrefijoManual}>
              {mostrarPrefijoManual && (
                <button type="button" data-testid="plantilla-agregar-prefijo-manual"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center gap-1.5 border-b border-border font-semibold text-[#216295]"
                  onClick={agregarManual}>
                  <Plus size={13} /> Agregar prefijo {prefijoCandidato}
                </button>
              )}
              {filtrados.map((r) => (
                <button key={r.id} data-testid={`agregar-rep-plantilla-${r.codigo_parte}`} type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex justify-between"
                  onClick={() => elegir(r)}>
                  <span><span className="font-mono font-semibold text-xs">{r.codigo_parte}</span> — {r.nombre}</span>
                  <span className="text-[10px] text-indigo-600 font-semibold">
                    {!esMaestra ? "código completo" : (tipo === "fijo" ? "fijo" : `prefijo ${(r.codigo_parte || "").split("-")[0]}`)}
                  </span>
                </button>
              ))}
            </AnchoredList>
            {esMaestra && tipo === "codigo" && (
              <p className="text-[11px] text-muted-foreground mt-1">Si escribe 5 caracteres se agrega ese prefijo directamente.</p>
            )}
            {esMaestra && tipo === "fijo" && (
              <p className="text-[11px] text-muted-foreground mt-1">Se usa el mismo repuesto y la misma cantidad en todos los modelos.</p>
            )}
          </div>
        )}

        {esMaestra && tipo === "bloque" && (
          <div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Input data-testid="plantilla-grupo-palabra" placeholder="Piezas que empiezan con… (ej. BEARING)"
                value={grupoPalabra} className="h-8 text-xs w-56"
                onChange={(e) => setGrupoPalabra(e.target.value)} />
              <Input data-testid="plantilla-grupo-bloques" placeholder="En los bloques… (ej. TRANSMISION, CARTER)"
                value={grupoBloques} className="h-8 text-xs w-64"
                onChange={(e) => setGrupoBloques(e.target.value)} />
              <Button type="button" size="sm" variant="outline" data-testid="plantilla-agregar-grupo-btn"
                disabled={!grupoPalabra.trim() || !grupoBloques.trim()}
                onClick={() => { onAgregarGrupo(grupoPalabra, grupoBloques); setGrupoPalabra(""); setGrupoBloques(""); }}
                className="h-8">
                <Plus size={13} className="mr-1" /> Agregar
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Al generar cada modelo se buscan esas piezas en su catálogo.</p>
          </div>
        )}
      </div>

      <div className="space-y-1">
        <Label className="text-sm font-semibold">2. Repuestos de la plantilla · {items.length}</Label>
        <div className="border border-border rounded-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tipo</TableHead>
                <TableHead>Repuesto</TableHead>
                <TableHead>Cantidad</TableHead>
                <TableHead>Máx. por trabajo</TableHead>
                <TableHead className="text-center">Opcional</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-6 text-sm">
                    Todavía no hay repuestos en esta plantilla.
                  </TableCell>
                </TableRow>
              )}
              {items.map((it, i) => {
                const esGrupo = !!it.grupo_palabra;
                if (esGrupo) {
                  return (
                    <TableRow key={i} className={it.opcional ? "border-dashed border-2 border-zinc-300 bg-zinc-50" : ""}>
                      <TableCell><Chip color="bg-violet-200 text-violet-800">Bloque</Chip></TableCell>
                      <TableCell className="text-sm">
                        Piezas "{it.grupo_palabra}" · bloques {(it.grupo_bloques || []).join(", ")}
                      </TableCell>
                      <TableCell><span className="text-xs text-muted-foreground">Según catálogo</span></TableCell>
                      <TableCell><span className="text-xs text-muted-foreground">—</span></TableCell>
                      <TableCell className="text-center">
                        <Checkbox data-testid={`opcional-item-grupo-${i}`} checked={!!it.opcional}
                          onCheckedChange={() => onToggleOpcional(i)} />
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onQuitar(i)}><Trash2 size={13} /></Button>
                      </TableCell>
                    </TableRow>
                  );
                }
                const rep = it.repuesto_id ? repIdx[it.repuesto_id] : null;
                const esFijo = !!it.fijo;
                const esPrefijo = esMaestra && !esFijo;
                let cod, nom;
                if (esPrefijo) {
                  cod = it.prefijo || (rep ? (rep.codigo_parte || "").split("-")[0] : "");
                  nom = it.nombre_referencia || rep?.nombre || it.nombre || "";
                } else {
                  cod = rep?.codigo_parte || it.codigo_parte || "";
                  nom = rep?.nombre || it.nombre || it.nombre_referencia || "";
                }
                return (
                  <TableRow key={i} className={it.opcional ? "border-dashed border-2 border-zinc-300 bg-zinc-50" : ""}>
                    <TableCell>
                      {esFijo ? <Chip color="bg-indigo-200 text-indigo-800">Fijo</Chip>
                        : esPrefijo ? <Chip color="bg-emerald-100 text-emerald-700">Por código</Chip>
                        : null}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{nom}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {esPrefijo ? `· prefijo ${cod}` : `· ${cod}`}
                      </div>
                    </TableCell>
                    <TableCell>
                      {esPrefijo ? (
                        it.cantidad_manual ? (
                          <div className="flex items-center gap-1.5">
                            <Input type="number" min="0" step="0.01" className="w-16 h-7" value={it.cantidad}
                              data-testid={`cantidad-item-${cod}`}
                              onChange={(e) => { const v = parseFloat(String(e.target.value).replace(",", ".")); onCantidad(i, v > 0 ? v : 1); }} />
                            <button type="button" data-testid={`cant-manual-item-${cod}`}
                              className="text-[11px] text-indigo-600 underline underline-offset-2 whitespace-nowrap"
                              onClick={() => onToggleCantidadManual(i)}>auto</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">Según catálogo</span>
                            <button type="button" data-testid={`cant-manual-item-${cod}`}
                              className="text-[11px] text-indigo-600 underline underline-offset-2 whitespace-nowrap"
                              onClick={() => onToggleCantidadManual(i)}>cambiar</button>
                          </div>
                        )
                      ) : (
                        <Input type="number" min="0" step="0.01" className="w-16 h-7" value={it.cantidad}
                          data-testid={`cantidad-item-${cod}`}
                          onChange={(e) => { const v = parseFloat(String(e.target.value).replace(",", ".")); onCantidad(i, v > 0 ? v : 1); }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {!esFijo && onCantidadMaxima ? (
                        <Input type="number" min="0" step="1" placeholder="—" className="w-14 h-7"
                          title="Cant. máx. por trabajo: tope para excluir automáticamente repuestos repetidos entre servicios por encima de lo que la moto usa. Vacío = sin tope (ej. aceites)."
                          data-testid={`cantidad-maxima-item-${cod}`} value={it.cantidad_maxima ?? ""}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const v = raw === "" ? null : parseFloat(String(raw).replace(",", "."));
                            onCantidadMaxima(i, v != null && v > 0 ? v : null);
                          }} />
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Checkbox data-testid={`opcional-item-${cod}`} checked={!!it.opcional}
                        onCheckedChange={() => onToggleOpcional(i)} />
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onQuitar(i)}><Trash2 size={13} /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
